// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyActiveStore, TyStoreLink } from "@/hooks/useStore/types";
import type { TyApiResponse } from "@/types";
import { pickLangValue, readLangs } from "@/utils";
import StoreLinkPopup from "@/UI/popups/StoreLinkPopup";
import SettingsCard from "@/UI/components/SettingsCard";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/** Mirrors `MAX_STORE_LINKS` in the backend — the API rejects anything past it. */
const MAX_STORE_LINKS = 12;

// --- Logic --------------------------------------------------------------------------------------

/**
 * The store's contact list: phone, WhatsApp, socials, a map pin — in whatever order the store puts
 * them, because that is the order the menu renders them in.
 *
 * The whole list saves in one request. Editing, reordering and removing all happen locally first, so
 * the menu never sees a list mid-rearrangement, and one cancel puts everything back.
 */
export default function SettingsStoreLinks() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages } = useStoreLanguages();
    const { canEdit } = usePermission();
    const addComp = usePopup((state) => state.addComp);
    const storeId = useStore((state) => state.storeId);
    const setStore = useStore((state) => state.setStore);

    const editable = canEdit("settings");

    const { data: storeData } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store");
    const {
        data: saveData,
        loading: saving,
        error: saveError,
        invoke: save,
    } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store", { method: "PUT" }, false);

    /**
     * What the server last confirmed. Every edit is shown at once and sent at once, so this is what
     * the list goes back to if the request is refused — a row that reordered on screen and silently
     * did not reorder in the menu is the one outcome worth guarding against.
     */
    const [saved, setSaved] = useState<TyStoreLink[] | null>(null);
    const [links, setLinks] = useState<TyStoreLink[]>([]);
    const [justSaved, setJustSaved] = useState(false);

    useEffect(() => {
        const fetched = storeData?.data?.store;
        if (!fetched) return;
        setSaved(fetched.links ?? []);
        setLinks(fetched.links ?? []);
    }, [storeData]);

    useEffect(() => {
        if (!saveData?.success) return;
        const updated = saveData.data.store;
        setSaved(updated.links ?? []);
        // The server assigns an `_id` to a new link, so its answer replaces the optimistic list
        // rather than merely confirming it.
        setLinks(updated.links ?? []);
        if (storeId) setStore(storeId, updated);
        setJustSaved(true);
        const timer = setTimeout(() => setJustSaved(false), 2200);
        return () => clearTimeout(timer);
    }, [saveData]);

    // A refused save puts the list back where it was, so what is on screen is always what is live.
    useEffect(() => {
        if (saveError && saved) setLinks(saved);
    }, [saveError]);

    const isFull = links.length >= MAX_STORE_LINKS;

    // --- Handlers --------------------------------------------------------------------------------

    /** Shown immediately and sent immediately — there is no save button to press. */
    const commit = (next: TyStoreLink[]) => {
        setLinks(next);
        save({ method: "PUT", data: { links: next } });
    };

    const handleSave = (link: TyStoreLink, index?: number) =>
        commit(index === undefined ? [...links, link] : links.map((entry, i) => (i === index ? link : entry)));

    const openEditor = (link?: TyStoreLink, index?: number) =>
        addComp({
            name: "store-link",
            Component: StoreLinkPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { link, index, onSave: handleSave },
        });

    const move = (index: number, by: number) => {
        const target = index + by;
        if (target < 0 || target >= links.length) return;
        const next = [...links];
        [next[index], next[target]] = [next[target], next[index]];
        commit(next);
    };

    const remove = (index: number) => commit(links.filter((_, i) => i !== index));

    return (
        <SettingsCard
            icon="/icons/store-icon.svg"
            title={t.storeLinks.title}
            subtitle={t.storeLinks.subtitle}
            badge={editable ? undefined : t.storeLinks.readOnlyBadge}
        >
            <div className="ssl-list">
                {/* No save button, so the card has to say for itself when it is writing and when it
                    has written — otherwise a reorder looks like nothing happened. */}
                {editable && (
                    <div className="ssl-status" aria-live="polite">
                        {saving && (
                            <>
                                <span className="ssl-spinner" />
                                {t.settings.savingButton}
                            </>
                        )}
                        {!saving && saveError && <span className="ssl-status-error">{saveError}</span>}
                        {!saving && !saveError && justSaved && (
                            <>
                                <img src="/icons/check-icon.svg" alt="" width={12} height={12} />
                                {t.settings.savedMessage}
                            </>
                        )}
                        {!saving && !saveError && !justSaved && <span className="ssl-status-hint">{t.storeLinks.autoSaveHint}</span>}
                    </div>
                )}

                {links.length === 0 && <p className="ssl-empty">{t.storeLinks.empty}</p>}

                {links.map((link, index) => {
                    const label = pickLangValue(readLangs(link.label, languages), lang ?? "ar");
                    return (
                        <div key={link._id ?? `${link.type}-${index}`} className="ssl-row" data-hidden={!link.isVisible}>
                            <div className="ssl-row-text">
                                <p className="ssl-row-type">
                                    {t.storeLinks.types[link.type]}
                                    {!link.isVisible && <span className="ssl-row-badge">{t.storeLinks.hiddenBadge}</span>}
                                </p>
                                <p className="ssl-row-value" dir="ltr">
                                    {link.value}
                                </p>
                                {label && <p className="ssl-row-label">{label}</p>}
                            </div>

                            {editable && (
                                <div className="ssl-row-actions">
                                    <button
                                        type="button"
                                        className="ssl-icon-btn"
                                        onClick={() => move(index, -1)}
                                        disabled={index === 0}
                                        aria-label={t.storeLinks.moveUpLabel}
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        className="ssl-icon-btn"
                                        onClick={() => move(index, 1)}
                                        disabled={index === links.length - 1}
                                        aria-label={t.storeLinks.moveDownLabel}
                                    >
                                        ↓
                                    </button>
                                    <button type="button" className="ssl-text-btn" onClick={() => openEditor(link, index)}>
                                        {t.storeLinks.editTitle}
                                    </button>
                                    <button type="button" className="ssl-text-btn ssl-text-btn--danger" onClick={() => remove(index)}>
                                        {t.storeLinks.removeLabel}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}

                {editable && (
                    <button type="button" className="ssl-add" onClick={() => openEditor()} disabled={isFull}>
                        {isFull ? t.storeLinks.maxReached : t.storeLinks.addLabel}
                    </button>
                )}
            </div>
        </SettingsCard>
    );
}
